import { useEffect, useRef, useState } from "react";
import { Link } from "react-scroll";
import { FiHome, FiBriefcase, FiLayers, FiUser, FiMail } from "react-icons/fi";
import "./BottomNav.css";

/*
  scrollTo  → matches <Element name="..."> in each page section (react-scroll)
  sectionId → the actual HTML id="..." used for IntersectionObserver
*/
const NAV_ITEMS = [
  { scrollTo: "home",     sectionId: "home-section", label: "Home",     Icon: FiHome },
  { scrollTo: "work",     sectionId: "work",         label: "Works",    Icon: FiBriefcase },
  { scrollTo: "services", sectionId: "services",     label: "Services", Icon: FiLayers },
  { scrollTo: "about",    sectionId: "about",        label: "About",    Icon: FiUser },
  { scrollTo: "contact",  sectionId: "contact",      label: "Contact",  Icon: FiMail },
];

export default function BottomNav() {
  const [hidden, setHidden]   = useState(false);
  const [active, setActive]   = useState("home"); // matches scrollTo key
  const lastScrollY           = useRef(0);
  const ticking               = useRef(false);

  /* ── YouTube-style hide on scroll-down, reveal on scroll-up ── */
  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const y     = window.scrollY;
        const delta = y - lastScrollY.current;

        if (y < 60) {
          setHidden(false);         // always visible near top
        } else if (delta > 8) {
          setHidden(true);          // scrolling down → slide away
        } else if (delta < -8) {
          setHidden(false);         // scrolling up   → reveal
        }

        lastScrollY.current = y;
        ticking.current = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Active section detection via IntersectionObserver ── */
  useEffect(() => {
    const ratioMap = {};

    const pickActive = () => {
      let best = "home";
      let bestRatio = -1;
      NAV_ITEMS.forEach(({ scrollTo, sectionId }) => {
        const key = sectionId;
        if ((ratioMap[key] ?? 0) > bestRatio) {
          bestRatio = ratioMap[key] ?? 0;
          best = scrollTo;
        }
      });
      setActive(best);
    };

    const observers = [];
    NAV_ITEMS.forEach(({ sectionId }) => {
      const el = document.getElementById(sectionId);
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => { ratioMap[e.target.id] = e.intersectionRatio; });
          pickActive();
        },
        { threshold: [0, 0.1, 0.2, 0.3, 0.5] }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <nav
      className={`bottom-nav${hidden ? " bottom-nav--hidden" : ""}`}
      aria-label="Mobile navigation"
    >
      {NAV_ITEMS.map(({ scrollTo, label, Icon }) => {
        const isActive = active === scrollTo;
        return (
          <Link
            key={scrollTo}
            to={scrollTo}          /* ← must match <Element name="..."> */
            href={`/#${scrollTo}`}
            spy={false}
            smooth
            duration={600}
            offset={0}
            className={`bottom-nav__item${isActive ? " bottom-nav__item--active" : ""}`}
            onClick={() => setActive(scrollTo)}
            aria-label={label}
            aria-current={isActive ? "page" : undefined}
          >
            <span className="bottom-nav__icon-wrap">
              <Icon size={22} className="bottom-nav__icon" />
              {isActive && <span className="bottom-nav__active-bg" />}
            </span>
            <span className="bottom-nav__label">{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
